import { getCurrentUserId, supabase, Transaction } from '../lib/supabase';
import { calculateBalanceFromTransactions } from '../lib/balanceUtils';
import { getLocalDateString, toLevyPeriodDate } from '../lib/dateUtils';
import AuditService from './AuditService';

type AccountLevyRate = {
  account_id: string;
  amount: number;
};

export const getTransactionTypeLabel = (type = 'other_charge') => {
  switch (type) {
    case 'monthly_levy': return 'Monthly Levy';
    case 'quarterly_levy': return 'Quarterly Levy';
    case 'special_levy': return 'Special Levy';
    case 'maintenance_levy': return 'Maintenance Levy';
    case 'payment': return 'Payment';
    case 'penalty': return 'Penalty';
    case 'interest': return 'Interest';
    case 'administrative_charge': return 'Administrative Charge';
    case 'repair_charge': return 'Repair / Approved Charge';
    case 'miscellaneous_charge': return 'Miscellaneous Charge';
    case 'credit_adjustment': return 'Credit Adjustment';
    case 'opening_balance': return 'Opening Balance';
    case 'reversal': return 'Reversal';
    default: return type.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
  }
};

export class LevyService {
  private bodyCorporateId: string;

  static isAccountLevyRatePermissionError(error: unknown): boolean {
    const supabaseError = error as { code?: string; message?: string } | null;
    return supabaseError?.code === '42501'
      && supabaseError.message === 'permission denied for table account_levy_rates';
  }

  static isMissingLevySchemaError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error || '');
    const normalized = message.toLowerCase();

    return normalized.includes('relation "levy_rules" does not exist')
      || normalized.includes('relation "account_levy_rates" does not exist')
      || normalized.includes('relation public.levy_rules does not exist')
      || normalized.includes('relation public.account_levy_rates does not exist')
      || normalized.includes('does not exist') && (normalized.includes('levy_rules') || normalized.includes('account_levy_rates'));
  }

  private static parseCalendarDate(date: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (!match) return null;

    const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return parsed.getFullYear() === Number(match[1])
      && parsed.getMonth() === Number(match[2]) - 1
      && parsed.getDate() === Number(match[3])
      ? parsed
      : null;
  }

  static isLevyPaymentLate(dueDate: string, asOfDate: Date, graceDays: number = 0): boolean {
    if (!dueDate || Number.isNaN(asOfDate.getTime())) return false;

    const parsedDue = LevyService.parseCalendarDate(dueDate);
    if (!parsedDue) return false;

    const effectiveDueDate = new Date(parsedDue);
    effectiveDueDate.setDate(effectiveDueDate.getDate() + Math.max(0, Number(graceDays) || 0));

    // Dates in this system are calendar dates, not instants. The whole due
    // date (the 7th by default) remains payable without a penalty.
    const paymentDate = new Date(asOfDate.getFullYear(), asOfDate.getMonth(), asOfDate.getDate());

    return paymentDate.getTime() > effectiveDueDate.getTime();
  }

  constructor(bodyCorporateId: string) {
    this.bodyCorporateId = bodyCorporateId;
  }

  getTransactionTypeForLevyType(type: string): Transaction['transaction_type'] {
    switch (type) {
      case 'quarterly':
        return 'quarterly_levy';
      case 'special':
        return 'special_levy';
      case 'maintenance':
        return 'maintenance_levy';
      case 'monthly':
      default:
        return 'monthly_levy';
    }
  }

  getChargeTypeForLevyType(type: string): string {
    return this.getTransactionTypeForLevyType(type);
  }

  /**
   * Generate a levy type for all active accounts.
   * Supports the model's levy rule taxonomy: monthly, quarterly, special, maintenance.
   */
  async generateLevyEntriesForType(levyType: 'monthly' | 'quarterly' | 'special' | 'maintenance', month?: Date) {
    const targetDate = month || new Date();
    const year = targetDate.getFullYear();
    const monthNum = targetDate.getMonth() + 1;
    const periodLabel = `${year}-${String(monthNum).padStart(2, '0')}`;
    const period = toLevyPeriodDate(periodLabel);
    const targetDateString = getLocalDateString(targetDate);
    const transactionType = this.getTransactionTypeForLevyType(levyType);

    try {
      const { data: accounts, error: accountsError } = await supabase
        .from('accounts')
        .select(`id, flat_id, account_number, flats (flat_number)`)
        .eq('body_corporate_id', this.bodyCorporateId)
        .eq('status', 'active');

      if (accountsError) throw accountsError;

      const accountIds = (accounts || []).map((account: { id: string }) => account.id);
      if (accountIds.length === 0) {
        return { created: 0, skipped: 0, total: 0 };
      }

      const { data: levyRules, error: rulesError } = await supabase
        .from('levy_rules')
        .select('*')
        .eq('body_corporate_id', this.bodyCorporateId)
        .eq('is_active', true)
        .eq('levy_type', levyType)
        .lte('effective_from', targetDateString)
        .or(`effective_to.is.null,effective_to.gte.${targetDateString}`)
        .order('effective_from', { ascending: false });

      if (rulesError) throw rulesError;

      const { data: accountRates, error: ratesError } = await supabase
        .from('account_levy_rates')
        .select('*')
        .in('account_id', accountIds)
        .lte('effective_from', targetDateString)
        .or(`effective_to.is.null,effective_to.gte.${targetDateString}`)
        .order('effective_from', { ascending: false });

      if (ratesError) throw ratesError;

      let created = 0;
      let skipped = 0;
      const currentUserId = await getCurrentUserId();

      for (const account of accounts || []) {
        const { data: existing, error: existingError } = await supabase
          .from('transactions')
          .select('id')
          .eq('account_id', account.id)
          .eq('transaction_type', transactionType)
          .eq('levy_period', period)
          .eq('status', 'posted')
          .single();

        if (existingError && existingError.code !== 'PGRST116') {
          console.error('Error checking existing levy:', existingError);
          continue;
        }

        if (existing) {
          skipped++;
          continue;
        }

        let amount = 0;
        const accountRate = accountRates?.find((r: AccountLevyRate) => r.account_id === account.id);
        if (accountRate) {
          amount = accountRate.amount;
        } else if (levyRules && levyRules.length > 0) {
          amount = levyRules[0].default_amount;
        }

        if (amount === 0) {
          console.warn(`No levy amount configured for account ${account.account_number}`);
          continue;
        }

        const configuredDueDay = Number(levyRules && levyRules.length > 0 ? levyRules[0].due_day : 7);
        const lastDayOfMonth = new Date(year, monthNum, 0).getDate();
        const dueDate = new Date(year, monthNum - 1, Math.min(Math.max(configuredDueDay || 7, 1), lastDayOfMonth));

        const { data: createdTransaction, error: insertError } = await supabase
          .from('transactions')
          .insert({
            body_corporate_id: this.bodyCorporateId,
            account_id: account.id,
            transaction_date: targetDateString,
            transaction_type: transactionType,
            description: `${levyType.charAt(0).toUpperCase() + levyType.slice(1)} Levy - ${periodLabel}`,
            reference_number: `LEV-${levyType.toUpperCase()}-${periodLabel}-${account.account_number}`,
            levy_period: period,
            due_date: getLocalDateString(dueDate),
            debit_amount: amount,
            credit_amount: 0,
            status: 'posted',
            created_by: currentUserId,
          })
          .select()
          .single();

        if (insertError) {
          console.error(`Error creating ${levyType} levy:`, insertError);
        } else {
          created++;
          if (currentUserId && createdTransaction) {
            await new AuditService(this.bodyCorporateId, currentUserId).logAction(
              'CREATE', 'transaction', createdTransaction.id, undefined, createdTransaction
            );
          }
        }
      }

      return { created, skipped, total: (accounts || []).length };
    } catch (error) {
      console.error(`Error generating ${levyType} levies:`, error);
      if (LevyService.isMissingLevySchemaError(error)) {
        throw new Error('The levy configuration tables are missing. Run the financial-engine SQL migration to create levy_rules and account_levy_rates.');
      }
      throw error;
    }
  }

  /**
   * Generate monthly levies for all active accounts.
   * This should be run on the 1st of each month or manually.
   */
  async generateMonthlyLevies(month?: Date) {
    return this.generateLevyEntriesForType('monthly', month);
  }

  /**
   * Check for late payments and apply penalties using the configured
   * grace days and the levy due date. The rule is: if a posted levy fell
   * behind its due date and the account has a remaining outstanding balance,
   * create a posted penalty row linked to that levy.
   */
  async applyLatePenalties(asOfDate?: Date, accountId?: string) {
    const checkDate = asOfDate || new Date();
    const today = getLocalDateString(checkDate);
    const currentUserId = await getCurrentUserId();

    try {
      const { data, error } = await supabase.rpc('apply_late_penalties', {
        p_body_corporate_id: this.bodyCorporateId,
        p_as_of_date: today,
        p_account_id: accountId || null,
        p_created_by: currentUserId,
      });

      if (error) throw error;
      return { penaltiesApplied: Number(data || 0) };
    } catch (error) {
      console.error('Error applying penalties:', error);
      throw error;
    }
  }

  async recordPayment(payment: {
    accountId: string;
    transactionDate: string;
    description: string;
    referenceNumber: string;
    amount: number;
    notes?: string;
  }) {
    const currentUserId = await getCurrentUserId();
    const { data, error } = await supabase.rpc('record_payment_with_allocations', {
      p_body_corporate_id: this.bodyCorporateId,
      p_account_id: payment.accountId,
      p_transaction_date: payment.transactionDate,
      p_description: payment.description,
      p_reference_number: payment.referenceNumber,
      p_amount: payment.amount,
      p_notes: payment.notes || null,
      p_created_by: currentUserId,
    });

    if (error) throw error;

    const result = Array.isArray(data) ? data[0] : data;
    return {
      paymentTransactionId: result?.payment_transaction_id as string | undefined,
      penaltiesApplied: Number(result?.penalties_applied || 0),
    };
  }

  /**
   * Get account balance with a database RPC first, but fall back to the posted ledger
   * when the stored function is missing so the UI never silently loses the balance.
   */
  async getAccountBalance(accountId: string): Promise<number> {
    const { data, error } = await supabase
      .rpc('get_account_balance', { p_account_id: accountId });

    if (!error && typeof data === 'number') {
      return data || 0;
    }

    console.warn('Falling back to posted ledger balance calculation for:', accountId, error);

    const { data: rows, error: fetchError } = await supabase
      .from('transactions')
      .select('debit_amount, credit_amount, status')
      .eq('account_id', accountId)
      .eq('status', 'posted');

    if (fetchError) {
      console.error('Error getting account balance from ledger:', fetchError);
      return 0;
    }

    return calculateBalanceFromTransactions(rows || []);
  }

  /**
   * Get all transactions for an account with running balance
   */
  async getAccountTransactions(accountId: string, fromDate?: string, toDate?: string) {
    let query = supabase
      .from('transactions')
      .select('*')
      .eq('account_id', accountId)
      .eq('status', 'posted')
      .order('transaction_date', { ascending: true })
      .order('created_at', { ascending: true });

    if (fromDate) {
      query = query.gte('transaction_date', fromDate);
    }
    if (toDate) {
      query = query.lte('transaction_date', toDate);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching transactions:', error);
      return [];
    }

    // Calculate running balance
    let runningBalance = 0;
    return (data || []).map((t: Transaction) => {
      const amount = (t.debit_amount || 0) - (t.credit_amount || 0);
      runningBalance += amount;
      return {
        ...t,
        running_balance: runningBalance,
      };
    });
  }
}

export default LevyService;
